import axios from 'axios'
import { graphql } from "gatsby"
import Cookies from "js-cookie"
import React, { useEffect, useState } from "react"

import { Layout } from "../components/Layout"
import {
	ActionSection,
	CallToActionSection,
	CarouselSection,
	FaqSection,
	HeaderSection,
	PartnerSection,
	QuizSection,
	VideoSection,
	FormSection
} from "../components/landing-page/Sections"
import { PartnersAlana } from '../components/landing-page/PartnersAlana'
import { formatDateToString } from "../utils/date"

function renderSectionByType(section, pageTitle, slug, user) {
	switch (section.type) {
		case "headerSection":
			return <HeaderSection key={section.order} section={section} pageTitle={pageTitle} slug={slug} user={user}/>
		case "actionSection":
			return <ActionSection key={section.order} section={section} pageTitle={pageTitle} user={user} />
		case "faqSection":
			return <FaqSection key={section.order} section={section} />
		case "partnerSection":
			return slug === "educacao-baseada-na-natureza" ? <PartnersAlana key={section.order} section={section} /> : <PartnerSection key={section.order} section={section} />
		case "callToActionSection":
			return <CallToActionSection key={section.order} section={section} pageTitle={pageTitle} user={user} />
		case "carouselSection":
			return <CarouselSection key={section.order} section={section} pageTitle={pageTitle} user={user}/>
		case "videoSection":
			return <VideoSection key={section.order} section={section} slug={slug}/>
		case "quizSection":
			return <QuizSection key={section.order} section={section} />
		case "formSection":
			return <FormSection key={section.order} section={section} />
		default:
			console.log('not found: ', section.type)
			return ""
	}
}

export function Head({ data: { landingPage } }) {
	const { nameSeo, name, description, slug, createdAt, updatedAt } = landingPage;

	const title = `${nameSeo ? nameSeo : name} | Nova Escola`;
	const url = `https://novaescola.org.br/tudo-sobre/${slug}`;
	const image = landingPage.sections[0]?.body?.imageUrl ? landingPage.sections[0].body.imageUrl : null;

	const escapeText = (text) => {
		if (!text) return "";
		
		return text.replace(/\\/g, "\\")
			.replace(/"/g, '\\"')
			.replace(/'/g, "\\'")
			.replace(/\\n/g, /\\/);
	};

	const breadcrumb = [
    {
      "@type": "ListItem",
      position: 1,
      item: {
        "@id": "https://novaescola.org.br/",
        name: "Nova Escola",
      },
    },
    {
      "@type": "ListItem",
      position: 2,
      item: {
        "@id": `${url}`,
        name: `${nameSeo ? nameSeo : name}`,
      },
    },
  ];

  const formattedBreadcrumbSchema = JSON.stringify(breadcrumb, null, 2);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${url}/`} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:site_name" content="Nova Escola" />
      <link rel="manifest" href="/manifest.json" />
			{/* Breadcrumb */}
			<script type="application/ld+json">
				{`{
					"@context": "https://schema.org",
					"@type": "BreadcrumbList",
					"itemListElement": ${formattedBreadcrumbSchema}
				}`}
			</script>
			{/* Article */}
			<script type="application/ld+json">
				{`{
					"@context":"https://schema.org",
					"@graph": [
						{
							"@type":"Organization",
							"@id": "https://novaescola.org.br/#organization",
							"name":"Nova Escola",
							"url":"https://novaescola.org.br/",
							"logo": {
								"@type": "ImageObject",
								"url": "https://novaescola.org.br/planos-de-aula/android-icon-192x192.png",
								"width": 192,
								"height": 192
							},
							"sameAs":[
								"https://www.facebook.com/novaescola",
								"https://twitter.com/novaescola",
								"https://www.linkedin.com/company/associação-nova-escola/",
								"https://www.youtube.com/user/revistanovaescola",
								"https://www.instagram.com/novaescola/"
							]
						},
						{
							"@type":"WebSite",
							"@id":"${url}#website",
							"url":"${url}",
							"name":"${title}"
						},
						{
							"@type":"WebPage",
							"@id": "${url}#webpage",
							"url":"${url}",
							"inLanguage":"pt-BR",
							"name":"${escapeText(title)}",
							"isAccessibleForFree": "False",
							"hasPart":{
								"@type":"WebPageElement",
								"isAccessibleForFree": "False"
							},
							"description":"${escapeText(description)}",
							"isPartOf":{
								"@id": "${url}#website"
							},
							"about":{
								"@id": "https://novaescola.org.br/#organization"
							},
							"breadcrumb": {
								"@type": "BreadcrumbList",
								"@id": "${url}#breadcrumb",
								"itemListElement": ${formattedBreadcrumbSchema}
							}
						},
						{
							"@type": "ImageObject",
							"@id": "${url}#primaryimage",
							"url": "${image}",
							"height":"1280",
							"width":"853",
							"caption": "${escapeText(title)}"
						},
						{
							"@type": "Article",
							"@id": "${url}#article",
							"isPartOf": {
								"@id": "${url}#webpage"
							},
							"headline": "Conteúdo: ${escapeText(title)}",
							"datePublished": "${formatDateToString(createdAt)}",
							"dateModified": "${formatDateToString(updatedAt)}",
							"description": "${escapeText(description)}",
							"inLanguage": "pt-BR",
							"publisher": {
								"@id": "https://novaescola.org.br/#organization"
							},
							"image": {
								"@id": "${url}#primaryimage"
							},
							"mainEntityOfPage": {
								"@type": "WebPage",
								"@id": "${url}"
							},
							"keywords": "${escapeText(title).split(' ').join(', ')}"
						}
					]
				}`}
			</script>
    </>
  );
}

const LandingPage = ({ data }) => {
	const [isClient, setIsClient] = useState(false)
  const [hasAlert, setHasAlert] = useState(false)
  const [showAlert, setShowAlert] = useState(true)

  useEffect(() => {
    setIsClient(true)
  }, [])
  const [user, setUser] = useState(undefined);

	const sendDataLayerViewItem = () => {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			'event': 'view_item',
			'item_id': data.landingPage.id,
			'item_name': data.landingPage.name,
			'item_category': 'gerador-de-lp',
		});
	};

  const fetchUserData = async () => {
    const bearer = Cookies.get("Bearer");

    if (bearer) {
      try {
				const response = await axios.get(`${process.env.GATSBY_API_ACCOUNT_URL}/user`, {
					headers: {
						'Bearer': `${bearer}`
					}
				});
        setUser(response.data);
      } catch (err) {
        setUser(null);
      }
    }
  };

	const sendTriggerEscolaDeRespeito = () => {
    const bearer = Cookies.get('Bearer');

		if (!bearer) return;

		const { slug } = data.landingPage;

		if (slug === 'escola-de-respeito' || slug === 'especial-consciencia-negra') {
			const pageUrl = `/tudo-sobre/${slug}/`;
			const viewingDate = new Date();

			axios.post(`${process.env.GATSBY_API_EMAILS_TRANSACIONAIS_URL}/pageView`, {
				token: bearer,
				pageUrl,
				viewingDate,
			});
		}
	}

	useEffect(() => {
		sendDataLayerViewItem();
		fetchUserData();
		sendTriggerEscolaDeRespeito();
	}, []);

	// Condicionais LP Manifesto Educação Antirracista (https://novaescola.org.br/tudo-sobre/escola-de-respeito/)
	const isLpManifestoAntirracista = data.landingPage.slug === "escola-de-respeito"
	return isClient && (
		<Layout
			heroClass={`${isLpManifestoAntirracista ? "no-hero" : "has-hero"}`}
			headerClass={`${isLpManifestoAntirracista ? "padding-header" : ""}`}
      setHasAlert={setHasAlert}
      hasAlert={hasAlert}
      showAlert={showAlert}
      setShowAlert={setShowAlert}
			lpId={data.landingPage.landingPageId}
		>
			<main id="landing-page" role="main">
				{data.landingPage.sections
					.sort((a, b) => a.order - b.order)
					.map(section => renderSectionByType(section, data.landingPage.nameSeo || data.landingPage.name, data.landingPage.slug, user))}
			</main>
		</Layout>
	)
}

export const query = graphql`
	query ($slug: String) {
		landingPage(slug: { eq: $slug }) {
			id
			landingPageId
			name
			nameSeo
			description
			slug
			published
			createdAt
			updatedAt
			sections {
				body {
					amplitudeEvent
					analyticsEvent
					backgroundColor
					buttonDisabledText
					buttonEnabledAt
					buttonEnabledText
					buttonLink
					buttonText
					btnPrimaryBackground 
					btnPrimaryColorText 
					btnSecondaryBackground 
					btnSecondaryColorText 
					haveSecondButton
					addRegistrationBlockingButton
					secondButtonDisabledText
					secondButtonEnabledAt
					secondButtonEnabledText
					secondButtonLink
					secondAddRegistrationBlockingButton
					sendPdfWhatsappSecondButton
					sendPdfWhatsappPrimaryButton
					textColor
					sectionId
					hideLinkButton
					faq {
						answerText
						questionText
					}
					imageAlign
					imageUrl
					videoLink
					quizCode
					partners {
						urlPartner
						imageUrl
						remoteImagePartner {
							childImageSharp {
								gatsbyImageData
							}
						}
					}
					partnersDescription
					partnersTitle
					remoteImage {
						childImageSharp {
							resize(width: 1200) {
								src
								height
								width
							}
							gatsbyImageData
						}
					}
					title
					subtitle
					itens {
						index
						indexSection
						contentUrl
						title
						textButton
						imageUrl
						line
						verticalImage
						horizontalImage
						sendPdfWhatsappPrimaryButton
					}
				}
				createdAt
				order
				type
				updatedAt
			}
		}
	}
`

export default LandingPage
